import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { BarcodeScanner } from '../services/BarcodeScanner'
import { SerialBarcodeScanner } from '../services/SerialBarcodeScanner'
import { barcodeEventEmitter } from '../services/BarcodeEventEmitter'
import { BarcodeEvent, ScannerConfig, KeyPressMetrics } from '../types/barcode'

interface BarcodeContextValue {
  isScanning: boolean
  scanner: BarcodeScanner | null
  startScanning: () => void
  stopScanning: () => void
  updateConfig: (config: Partial<ScannerConfig>) => void
  calibrate: () => KeyPressMetrics | null
  resetCalibration: () => void
}

const BarcodeContext = createContext<BarcodeContextValue | null>(null)

export const useBarcodeContext = () => {
  const context = useContext(BarcodeContext)
  if (!context) {
    throw new Error('useBarcodeContext must be used within a BarcodeProvider')
  }
  return context
}

interface BarcodeProviderProps {
  children: React.ReactNode
  initialConfig?: Partial<ScannerConfig>
}

export const BarcodeProvider: React.FC<BarcodeProviderProps> = ({ children, initialConfig }) => {
  const scannerRef = useRef<BarcodeScanner | null>(null)
  const serialScannerRef = useRef<SerialBarcodeScanner | null>(null)
  // Default and initial merged config (including initialConfig prop)
  const initialMergedConfig: ScannerConfig = {
    timeToScan: 300,
    enableAudio: false,
    enableDebugMode: false,
    carriersConfig: [],
    ...initialConfig,
  }
  const [config, setConfig] = useState<ScannerConfig>(initialMergedConfig)
  const [isScanning, setIsScanning] = useState(false)

  useEffect(() => {
    // Merge saved config from localStorage once on mount
    const savedConfigStr = localStorage.getItem('barcode-scanner-config')
    let mergedConfig = initialMergedConfig
    if (savedConfigStr) {
      try {
        const parsed = JSON.parse(savedConfigStr)
        mergedConfig = { ...initialMergedConfig, ...parsed }
      } catch (error) {
        console.error('Failed to load scanner config:', error)
      }
    }
    // Update state with merged config
    setConfig(mergedConfig)

    // Create scanner instance with merged config
    const scanner = new BarcodeScanner(mergedConfig)
    scannerRef.current = scanner

    // Forward scanner events to global emitter
    const forwardScan = (event: BarcodeEvent) => {
      console.log('🪄 emitting barcode from forwardScan', event)
      barcodeEventEmitter.emit('scan', event)
    }
    scanner.subscribe('emitter', forwardScan)

    // Auto-start scanner
    scanner.start()
    setIsScanning(true)

    // Start serial scanner if running in NW.js desktop (window._depoto available)
    let serialScanner: SerialBarcodeScanner | null = null
    if (SerialBarcodeScanner.isAvailable()) {
      serialScanner = new SerialBarcodeScanner()
      serialScannerRef.current = serialScanner
      serialScanner.subscribe('emitter', forwardScan)
      serialScanner.start().catch(e => console.warn('SerialBarcodeScanner start failed:', e))
    }

    // Intercept NW.js window close so we can send the disable command before the process dies.
    // NW.js won't actually close the window until win.close(true) is called.
    const nwWin = (window as any)._depoto?.nw?.win
    const handleNwClose = async () => {
      if (serialScannerRef.current) {
        await serialScannerRef.current.stop().catch(() => null)
      }
      nwWin.close(true)
    }
    if (nwWin && serialScanner) {
      nwWin.on('close', handleNwClose)
    }

    return () => {
      scanner.unsubscribe('emitter', forwardScan)
      scanner.stop()
      if (serialScannerRef.current) {
        serialScannerRef.current.unsubscribe('emitter', forwardScan)
        serialScannerRef.current.stop()
        serialScannerRef.current = null
      }
      if (nwWin && serialScanner) {
        nwWin.removeListener('close', handleNwClose)
      }
    }
  }, [])

  const startScanning = useCallback(() => {
    if (scannerRef.current && !isScanning) {
      scannerRef.current.start()
      setIsScanning(true)
    }
  }, [isScanning])

  const stopScanning = useCallback(() => {
    if (scannerRef.current && isScanning) {
      scannerRef.current.stop()
      setIsScanning(false)
    }
  }, [isScanning])

  const updateConfig = useCallback((config: Partial<ScannerConfig>) => {
    if (scannerRef.current) {
      scannerRef.current.updateConfig(config)
      const newConfig = scannerRef.current.getConfig()
      setConfig(newConfig)

      // Save to localStorage (excluding RegExp objects)
      const configToSave = {
        timeToScan: newConfig.timeToScan,
        enableAudio: newConfig.enableAudio,
        enableDebugMode: newConfig.enableDebugMode,
      }
      localStorage.setItem('barcode-scanner-config', JSON.stringify(configToSave))
    }
  }, [])

  const calibrate = useCallback((): KeyPressMetrics | null => {
    if (scannerRef.current) {
      try {
        return scannerRef.current.calibrate()
      } catch (error) {
        console.error('Calibration failed:', error)
        return null
      }
    }
    return null
  }, [])

  const resetCalibration = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.resetCalibration()
    }
  }, [])

  const value: BarcodeContextValue = {
    isScanning,
    scanner: scannerRef.current,
    startScanning,
    stopScanning,
    updateConfig,
    calibrate,
    resetCalibration,
  }

  return <BarcodeContext.Provider value={value}>{children}</BarcodeContext.Provider>
}
